import {
  countPathPrefixParts,
  decodeRouteSegment,
  encodeRouteSegment,
  isRouteSafeSegment,
  joinPath,
} from "../../../hooks/routedOverlays/routedOverlayUtils";
import type {
  DrawerChain,
  DrawerRouteDefinition,
  DrawerRouteLevel,
} from "./types";

const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Path of a new `{segment}/{id}` level nested under `tipPath`, without a tab, or
 * `undefined` when the id cannot round-trip through the URL ("", "." or "..").
 */
export const buildLevelPath = (
  tipPath: string,
  segment: string,
  id: string | number,
): string | undefined =>
  isRouteSafeSegment(id)
    ? joinPath(tipPath, segment, encodeRouteSegment(id))
    : undefined;

/**
 * Parses the drawer levels in `pathname`. The first registered segment after `pathPrefix`
 * (when `pathname` starts with it) starts the chain, so a site prefix equal to a segment is
 * not read as a level. Each level is `{segment}/{id}`, followed by a tab only when the tab
 * is registered for that segment. The chain ends at an unknown segment (such as a dialog)
 * or a missing or empty id. Stacked levels (those with a `component`) beyond
 * `maxStackedLevels` are dropped.
 */
export const parseDrawerChain = (
  pathname: string,
  routes: readonly DrawerRouteDefinition[],
  maxStackedLevels = Number.POSITIVE_INFINITY,
  pathPrefix?: string,
): DrawerChain => {
  // Keep empty parts so "product//stock" ends the chain rather than reading "stock" as the id.
  const parts = pathname.split("/");
  if (parts[0] === "") {
    parts.shift();
  }
  if (parts[parts.length - 1] === "") {
    parts.pop();
  }
  const bySegment = new Map(routes.map((route) => [route.segment, route]));
  const normalizedPrefix = joinPath(pathPrefix);
  const prefix = normalizedPrefix === "/" ? undefined : normalizedPrefix;
  const prefixEnd = countPathPrefixParts(parts, prefix);
  const start = parts.findIndex(
    (part, index) => index >= prefixEnd && bySegment.has(part),
  );
  const basePath = joinPath(...(start >= 0 ? parts.slice(0, start) : parts));
  const levels: DrawerRouteLevel[] = [];
  let tipPath = basePath;
  let stackedCount = 0;

  for (let index = start; index >= 0 && index < parts.length; ) {
    const definition = bySegment.get(parts[index]);
    const rawId = parts[index + 1];
    if (!definition || !rawId) {
      break;
    }
    if (definition.component) {
      if (stackedCount >= maxStackedLevels) {
        break;
      }
      stackedCount += 1;
    }
    const path = joinPath(tipPath, definition.segment, rawId);
    const nextPart = parts[index + 2];
    const tab =
      nextPart !== undefined && definition.tabs?.includes(nextPart)
        ? nextPart
        : undefined;
    levels.push({
      segment: definition.segment,
      id: decodeRouteSegment(rawId),
      tab,
      parentPath: tipPath,
      path,
    });
    tipPath = tab ? joinPath(path, tab) : path;
    index += tab ? 3 : 2;
  }

  if (levels.length === 0) {
    const pagePath = joinPath(...parts);
    return {
      basePath: pagePath,
      levels,
      tipPath: pagePath,
      pathPrefix: prefix,
    };
  }
  return { basePath, levels, tipPath, pathPrefix: prefix };
};

/** Throws if segments or tabs are not unique kebab-case slugs, or a segment is also a tab. */
export const validateDrawerRoutes = (
  routes: readonly DrawerRouteDefinition[],
): void => {
  const problems: string[] = [];
  const segments = new Set<string>();
  const allTabs = new Set<string>();

  for (const { segment, tabs = [] } of routes) {
    if (!KEBAB_CASE.test(segment)) {
      problems.push(`Segment "${segment}" is not kebab-case.`);
    }
    if (segments.has(segment)) {
      problems.push(`Segment "${segment}" is registered more than once.`);
    }
    segments.add(segment);
    const segmentTabs = new Set<string>();
    for (const tab of tabs) {
      if (!KEBAB_CASE.test(tab)) {
        problems.push(`Tab "${tab}" of "${segment}" is not kebab-case.`);
      }
      if (segmentTabs.has(tab)) {
        problems.push(
          `Tab "${tab}" is listed more than once for "${segment}".`,
        );
      }
      segmentTabs.add(tab);
      allTabs.add(tab);
    }
  }
  for (const segment of segments) {
    if (allTabs.has(segment)) {
      problems.push(
        `Segment "${segment}" is also a tab, so the URL would be ambiguous.`,
      );
    }
  }

  if (problems.length > 0) {
    throw new Error(`Invalid drawer routes:\n- ${problems.join("\n- ")}`);
  }
};
